// AddParticipantOverlay — modal-on-modal that lets the current caller
// invite another TransferPitch user into the active call.
//
// Implementation choice: we re-use the existing chat-invitation pattern
// rather than building a new "ring user" API. The flow is:
//
//   1. We load the caller's ACTIVE 1:1 channels — the only people who can
//      be invited are people the caller already has an active thread with
//      (no global user directory search). The search field filters that
//      list locally.
//   2. User clicks "Add" → we send a `call_invitation` attachment (with
//      our existing call_id + call_type) into an EXISTING channel: the
//      thread the call was started from when the invitee is a member of
//      it, else their canonical `direct_*` DM, else the thread they were
//      listed from. Routing lives in inviteChannel.js — nothing is ever
//      created here (a members-only channel() create used to mint a shadow
//      `!members-<hash>` channel parallel to the canonical threads).
//   3. The other user's Inbox listener (`useInbox.handleIncomingCall`)
//      sees that attachment and shows their IncomingCallScreen — same
//      machinery as a fresh call invite, just landing on an existing
//      call_id.
//
// Caveat: Stream Video doesn't auto-add the new user as a member of the
// call; they join as a guest when they accept. For our use case (1:1 calls
// becoming multi-party) that's fine.

import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

import { TP, I, Icon, Avatar } from "./shared";
import {
  buildInviteCandidates,
  resolveInviteChannel,
} from "./inviteChannel";

// Light variant of the chrome close button — the shared one was tuned
// for dark overlays. White panels need a darker icon on a soft grey
// hover state.
const lightChromeBtn = {
  background: "#FFFFFF",
  border: "1px solid #E5E7EB",
  color: "#6B7280",
  width: 28,
  height: 28,
  borderRadius: 8,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
};

export default function AddParticipantOverlay({
  onClose,
  call,        // active call object — used for call.id + call.type
  chatClient,  // passed in from CallModal because <StreamCall> lives outside <Chat>
}) {
  const [query, setQuery] = useState("");
  const [channels, setChannels] = useState([]);

  // Load the caller's most recent channels. This is the ONLY invitee
  // source: people the caller shares an active 1:1 thread with (the
  // filter itself — active, 1:1, deduped — lives in inviteChannel.js).
  useEffect(() => {
    if (!chatClient) return;
    let cancelled = false;
    (async () => {
      try {
        const loaded = await chatClient.queryChannels(
          {
            type: "messaging",
            members: { $in: [chatClient.userID] },
          },
          [{ last_message_at: -1 }],
          { limit: 30, state: false, watch: false, presence: false }
        );
        if (cancelled) return;
        setChannels(loaded);
      } catch (err) {
        console.error("Failed to load contacts:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [chatClient]);

  // One row per counterpart of an active 1:1 thread; the search field
  // narrows this list locally — there is no platform-wide user search.
  const candidates = useMemo(
    () => buildInviteCandidates(channels, chatClient?.userID),
    [channels, chatClient]
  );
  const needle = query.trim().toLowerCase();
  const visible = needle
    ? candidates.filter((c) =>
        String(c.name || "").toLowerCase().includes(needle)
      )
    : candidates;

  // Click → send call_invitation to the person via an EXISTING channel:
  // the call's origin thread when they are a member of it, else their
  // canonical direct_* DM, else the thread they were listed from (see
  // inviteChannel.js). NEVER a members-only channel() create — Stream does
  // not derive the sorted direct_* pair id from a member list; it would
  // mint a distinct shadow channel (`!members-<hash>`) instead.
  const handleInvite = async (candidate) => {
    if (!chatClient || !call) return;
    try {
      const channel = await resolveInviteChannel({
        chatClient,
        call,
        candidate,
        channels,
      });
      await channel.sendMessage({
        text: call.type === "default" ? "📞 Call invitation" : "Call invitation",
        attachments: [
          {
            type: "call_invitation",
            call_id: call.id,
            call_type: call.type || "default",
            caller_id: chatClient.userID,
            caller_name: chatClient.user?.name || chatClient.userID,
          },
        ],
      });
      // Visual confirmation by marking the row as "added" — handled in
      // PersonRow's local state.
      return true;
    } catch (err) {
      console.error("Failed to invite participant:", err);
      toast.error("Could not send invite. Please try again.");
      return false;
    }
  };

  return (
    <div
      role="dialog"
      aria-label="Add participant"
      className="tp-add-participant-overlay"
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(8,10,20,0.55)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 25,
        padding: 24,
      }}
    >
      <div
        style={{
          width: 380,
          maxWidth: "100%",
          background: "#FFFFFF",
          border: `1px solid ${TP.border}`,
          borderRadius: 16,
          boxShadow: "0 24px 64px rgba(20,40,80,0.22)",
          overflow: "hidden",
          color: TP.text,
        }}
      >
        <div
          style={{
            padding: "14px 16px",
            borderBottom: `1px solid ${TP.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 600, color: TP.text }}>
            Add participant
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={lightChromeBtn}
          >
            <Icon d={I.close} size={14} />
          </button>
        </div>

        <div style={{ padding: 14 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 12px",
              background: TP.surface,
              border: `1px solid ${TP.border}`,
              borderRadius: 10,
            }}
          >
            <Icon d={I.search} size={16} />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search users…"
              aria-label="Search users to invite"
              style={{
                background: "transparent",
                border: "none",
                outline: "none",
                color: TP.text,
                flex: 1,
                fontSize: 13,
              }}
            />
          </div>
        </div>

        <div style={{ padding: "0 8px 8px", maxHeight: 320, overflowY: "auto" }}>
          <Section title={needle ? "Results" : "Recently contacted"}>
            {visible.length === 0 ? (
              <div
                style={{
                  padding: "10px 12px",
                  fontSize: 12,
                  color: TP.text3,
                }}
              >
                {needle
                  ? "No users found"
                  : "You can invite people you have an active chat with."}
              </div>
            ) : (
              visible.map((c) => (
                <PersonRow
                  key={c.id}
                  name={c.name}
                  role={c.role}
                  image={c.image}
                  onInvite={() => handleInvite(c)}
                />
              ))
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div style={{ padding: "6px 6px 4px" }}>
    <div
      style={{
        fontSize: 10,
        fontWeight: 600,
        color: TP.text3,
        padding: "6px 8px",
        letterSpacing: 0.6,
        textTransform: "uppercase",
      }}
    >
      {title}
    </div>
    {children}
  </div>
);

function PersonRow({ name, role, image, onInvite }) {
  const [state, setState] = useState("idle"); // idle | sending | added | error
  const handle = async () => {
    if (state !== "idle") return;
    setState("sending");
    const ok = await onInvite();
    setState(ok ? "added" : "error");
    if (!ok) {
      // Reset back to idle after a moment so user can retry.
      setTimeout(() => setState("idle"), 1500);
    }
  };
  return (
    <div
      className="tp-hover"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 8px",
        borderRadius: 10,
      }}
    >
      {image ? (
        <img
          src={image}
          alt=""
          style={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            objectFit: "cover",
            flexShrink: 0,
          }}
        />
      ) : (
        <Avatar name={name} size={36} />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: TP.text,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {name}
        </div>
        {role && (
          <div
            style={{
              fontSize: 11,
              color: TP.text2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {role}
          </div>
        )}
      </div>
      <button
        onClick={handle}
        disabled={state !== "idle"}
        style={{
          padding: "6px 12px",
          borderRadius: 8,
          fontSize: 12,
          fontWeight: 600,
          cursor: state === "idle" ? "pointer" : "default",
          background:
            state === "added"
              ? "rgba(16,185,129,0.18)"
              : state === "error"
              ? "rgba(239,68,68,0.18)"
              : TP.blue,
          border:
            state === "added"
              ? `1px solid ${TP.ok}`
              : state === "error"
              ? `1px solid ${TP.danger}`
              : "none",
          color:
            state === "added"
              ? TP.ok
              : state === "error"
              ? TP.danger
              : "#fff",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        {state === "added" ? (
          <>
            <Icon d={I.check} size={12} stroke={3} /> Invited
          </>
        ) : state === "error" ? (
          "Retry"
        ) : state === "sending" ? (
          "…"
        ) : (
          <>
            <Icon d={I.plus} size={12} stroke={3} /> Add
          </>
        )}
      </button>
    </div>
  );
}
